// §7.4 / §16 — the sync client's transport, over `fetch`.
//
// The counterpart to `SyncTransport`'s interface: M12.7 kept the protocol free of any particular
// way of talking to a server so two devices could be simulated independently (D-158). This is the
// one that talks to the real one.
//
// Every method that can legitimately answer "no" does, rather than throwing. A `HEAD` that 404s is
// not an error — it is §7.4 telling the client to upload — and a `PUT` that 409s is D-045's
// conflict, which is a *result*. Only a genuine transport failure throws, because that is what the
// caller must not mistake for an answer.
import type { VfsPath } from '@hangar/shared';
import { syncChangesResponseSchema, type SyncChangesResponse } from '@hangar/shared';
import type { SyncTransport } from './sync-client';

export class SyncTransportError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number | undefined) {
    super(message);
    this.name = 'SyncTransportError';
    this.status = status;
  }
}

const request = (path: string, init?: RequestInit): Promise<Response> =>
  fetch(path, { ...init, credentials: 'same-origin' });

export function createFetchSyncTransport(): SyncTransport {
  return {
    async changes(since: number): Promise<SyncChangesResponse> {
      const response = await request(`/api/sync/changes?since=${since}`);
      if (!response.ok) {
        throw new SyncTransportError('could not read changes', response.status);
      }
      // Parsed, not cast (§22): this drives what gets written to the user's disk.
      const parsed = syncChangesResponseSchema.safeParse(await response.json());
      if (!parsed.success) throw new SyncTransportError('the change feed was malformed');
      return parsed.data;
    },

    async hasBlob(sha: string): Promise<boolean> {
      const response = await request(`/api/blobs/${sha}`, { method: 'HEAD' });
      // 204 present, 404 absent (D-154). Anything else is a transport problem, and answering
      // "absent" to it would turn a broken server into a silent full re-upload of everything.
      if (response.status === 204) return true;
      if (response.status === 404) return false;
      throw new SyncTransportError('could not probe the blob', response.status);
    },

    async putBlob(sha: string, bytes: Uint8Array): Promise<void> {
      const response = await request(`/api/blobs/${sha}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/octet-stream' },
        body: bytes as unknown as BodyInit,
      });
      if (!response.ok) throw new SyncTransportError('the upload was refused', response.status);
    },

    async getBlob(sha: string): Promise<Uint8Array> {
      const response = await request(`/api/blobs/${sha}`);
      if (!response.ok) throw new SyncTransportError('could not fetch the blob', response.status);
      return new Uint8Array(await response.arrayBuffer());
    },

    async putFile(path: VfsPath, sha: string, size: number, ifMatch: number | undefined) {
      const response = await request(`/api/files?path=${encodeURIComponent(path)}`, {
        method: 'PUT',
        headers: {
          'content-type': 'application/json',
          ...(ifMatch === undefined ? {} : { 'if-match': `"${ifMatch}"` }),
        },
        body: JSON.stringify({ sha, size }),
      });

      // D-045 — not a failure. The client turns this into a conflict copy.
      if (response.status === 409) return { ok: false as const, reason: 'conflict' as const };
      if (!response.ok) throw new SyncTransportError('the write was refused', response.status);

      const body = (await response.json()) as { rev?: unknown };
      if (typeof body.rev !== 'number') throw new SyncTransportError('no revision came back');
      return { ok: true as const, rev: body.rev };
    },

    // D-194 — `POST /api/files/mkdir`. Idempotent server-side, so a folder two devices both
    // created answers the existing rev rather than a 409.
    async putDir(path: VfsPath) {
      const response = await request('/api/files/mkdir', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ path }),
      });

      // A *file* is at that path. Not a content conflict — a folder has no content — so the
      // client drops the entry rather than making a conflict copy of nothing.
      if (response.status === 409) return { ok: false as const, reason: 'conflict' as const };
      if (!response.ok) throw new SyncTransportError('the folder was refused', response.status);

      const body = (await response.json()) as { rev?: unknown };
      if (typeof body.rev !== 'number') throw new SyncTransportError('no revision came back');
      return { ok: true as const, rev: body.rev };
    },

    async deleteFile(path: VfsPath) {
      const response = await request(`/api/files?path=${encodeURIComponent(path)}`, {
        method: 'DELETE',
      });
      // 404 is an answer, not a failure: the path was created and deleted between two runs, so
      // the server is already in the state the tombstone was asking for.
      if (response.status === 404) return 'missing' as const;
      if (!response.ok) throw new SyncTransportError('the delete was refused', response.status);
      return 'deleted' as const;
    },
  };
}

/** §7.4 — the client hashes its own bytes to name a blob. Same algorithm as the server's (D-047). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as unknown as BufferSource);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
